import { useState, useCallback, useRef } from 'react'
import { InjectedModalProps } from 'vvs-uikit'
import PixelatedImage from 'views/VvsGotchi/components/PixelatedImage'
import { useTranslation } from 'contexts/Localization'
import { toastTypes } from 'components/Toast'
import type { History } from 'history'
import useToast from 'hooks/useToast'
import { Gachapon } from 'state/vvsgotchi/hooks/useGachapon'
import * as ga from 'utils/googleAnalyticsHelper'

import { GachaponStep, GachaponOption } from '../types'

import Requirements from './Requirements'
import Selection from './Selection'
import Rolling from './Rolling'
import Result from './Result'
import Redeemed from './Redeemed'

import { GachaponBackground } from './styles'

const GachaponModal: React.FC<
  InjectedModalProps & {
    gachapon: Gachapon
    redeemGachapon: (type: GachaponOption) => Promise<number>
    history: History
  }
> = ({ onDismiss, gachapon, redeemGachapon, history }) => {
  const { t } = useTranslation()
  const { toastVVSGotchi } = useToast()
  const [step, setStep] = useState<GachaponStep>('init')
  const [selection, setSelection] = useState<GachaponOption>(null)
  const [resultPoints, setResultPoints] = useState<number>(0)
  const rollingAnimationPlayedRef = useRef<() => any>(() => undefined)
  const { refresh } = gachapon

  const gachaponRoll = useCallback(
    async (sel: GachaponOption) => {
      setSelection(sel)
      const rollingAnimationPlayedPromise = new Promise<void>((resolve) => {
        rollingAnimationPlayedRef.current = resolve
        setStep('rolling')
      })

      try {
        ga.gotchiGachaponDraw(sel)
        const redeemedPoints = await redeemGachapon(sel)
        setResultPoints(redeemedPoints)
        await refresh()

        await rollingAnimationPlayedPromise

        setStep('result')
      } catch (err) {
        console.error(err)
        toastVVSGotchi(
          t('Something went wrong! (ಥ﹏ಥ)'),
          toastTypes.INFO,
          <PixelatedImage src="/images/vvsGotchi/cross.png" width="40px" />,
        )
        onDismiss()
      }
    },
    [onDismiss, redeemGachapon, refresh, t, toastVVSGotchi],
  )

  switch (step) {
    case 'init':
      if (gachapon.hasRedeemGacha) {
        return <Redeemed onDismiss={onDismiss} />
      }
      return (
        <>
          <GachaponBackground />
          <Requirements onDismiss={onDismiss} setStep={setStep} gachapon={gachapon} history={history} />
        </>
      )

    case 'select':
      return (
        <>
          <GachaponBackground />
          <Selection onDismiss={onDismiss} onSelect={gachaponRoll} />
        </>
      )

    case 'rolling':
    case 'result':
      return (
        <>
          <GachaponBackground show />
          <Rolling selection={selection} onAnimationPlayedRef={rollingAnimationPlayedRef} />
          {step === 'result' && <Result onDismiss={onDismiss} selection={selection} resultPoints={resultPoints} />}
        </>
      )

    default:
      return null
  }
}

export default GachaponModal
